/**
 * Offset, in milliseconds, between UTC and `timeZone` at the given instant.
 * Positive east of Greenwich, negative west of it.
 */
const zoneOffsetMs = (instantMs: number, timeZone: string): number => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(new Date(instantMs));

    const read = (type: Intl.DateTimeFormatPartTypes): number =>
        Number(parts.find((part) => part.type === type)?.value);

    const wallClockAsUtc = Date.UTC(
        read('year'),
        read('month') - 1,
        read('day'),
        read('hour'),
        read('minute'),
        read('second'),
    );
    // The formatted parts carry no milliseconds, so compare against a
    // second-truncated instant.
    return wallClockAsUtc - (instantMs - (instantMs % 1000));
};

/**
 * Converts a wall-clock timestamp (`YYYY-MM-DDTHH:mm:ss`, no offset) read in
 * `timeZone` into the matching UTC epoch in milliseconds.
 *
 * A zone's offset is only knowable for a concrete instant, so the offset is
 * applied twice: the first pass can land on the wrong side of a daylight-saving
 * transition, and the second pass re-reads the offset at that corrected
 * instant.
 */
export const resolveZonedInstant = (
    wallClock: string,
    timeZone: string,
): number => {
    const wallClockAsUtc = Date.parse(`${wallClock}Z`);
    const firstPass = wallClockAsUtc - zoneOffsetMs(wallClockAsUtc, timeZone);
    return wallClockAsUtc - zoneOffsetMs(firstPass, timeZone);
};
